<template>
  <div class="chat-container" :class="containerClasses">
    <!-- Header -->
    <div v-if="showHeader && currentConversation" class="chat-header">
      <div class="chat-header-content">
        <div class="chat-avatar">
          <img
            v-if="conversationAvatar"
            :src="conversationAvatar"
            :alt="conversationName"
            class="avatar-image"
          />
          <div v-else class="avatar-placeholder">
            {{ conversationInitials }}
          </div>
        </div>

        <div class="chat-info">
          <h3 class="chat-title">{{ conversationName }}</h3>
          <div class="chat-subtitle">
            <span v-if="onlineStatus" class="online-indicator" />
            {{ onlineStatus || participantCount }}
          </div>
        </div>
      </div>

      <div class="chat-actions">
        <button
          v-if="showSearch"
          @click="toggleSearch"
          class="action-button"
          title="Search"
        >
          🔍
        </button>
        <button
          v-if="showInfo"
          @click="showInfoPanel"
          class="action-button"
          title="Info"
        >
          ℹ️
        </button>
        <button
          @click="$emit('close')"
          class="action-button close-button"
          title="Close"
        >
          ✕
        </button>
      </div>
    </div>

    <!-- Search Bar -->
    <div v-if="isSearchOpen" class="chat-search">
      <input
        v-model="searchQuery"
        type="text"
        placeholder="Search messages..."
        class="search-input"
        @input="handleSearch"
      />
      <button @click="toggleSearch" class="search-close">✕</button>
    </div>

    <!-- Messages Area -->
    <div class="chat-messages" ref="messagesContainer">
      <!-- Loading State -->
      <div v-if="isLoading && messages.length === 0" class="loading-container">
        <div class="loading-spinner"></div>
        <p>Loading messages...</p>
      </div>

      <!-- Empty State -->
      <div v-else-if="!hasMessages && !isLoading" class="empty-state">
        <div class="empty-icon">💬</div>
        <h3>No messages yet</h3>
        <p>Start the conversation with a message!</p>
      </div>

      <!-- Messages List -->
      <MessageList
        v-else
        :messages="filteredMessages"
        :current-user="currentUser"
        :is-loading="isLoading"
        @load-more="loadMoreMessages"
        @message-action="handleMessageAction"
      />

      <!-- Typing Indicator -->
      <TypingIndicator
        v-if="typingIndicator"
        :text="typingIndicator"
      />
    </div>

    <!-- Input Area -->
    <div class="chat-input-container">
      <MessageInput
        v-model="inputText"
        :disabled="!isConnected || isLoading"
        :placeholder="inputPlaceholder"
        :show-send-button="showSendButton"
        :max-length="maxLength"
        @send="handleSendMessage"
        @typing-start="handleTypingStart"
        @typing-stop="handleTypingStop"
        @attachment="handleAttachment"
      />
    </div>

    <!-- Error Display -->
    <div v-if="error" class="chat-error">
      <span class="error-icon">⚠️</span>
      <span class="error-text">{{ error.message }}</span>
      <button @click="error = null" class="error-close">✕</button>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, nextTick, onMounted } from 'vue'
import { useChat } from '../composables/useChat.js'
import MessageList from './MessageList.vue'
import MessageInput from './MessageInput.vue'
import TypingIndicator from './TypingIndicator.vue'

export default {
  name: 'ChatContainer',
  components: {
    MessageList,
    MessageInput,
    TypingIndicator
  },
  props: {
    // Configuration du chat
    conversationId: {
      type: String,
      default: null
    },

    // Participants pour nouvelle conversation
    participants: {
      type: Array,
      default: () => []
    },

    // Options d'affichage
    showHeader: {
      type: Boolean,
      default: true
    },
    showSearch: {
      type: Boolean,
      default: true
    },
    showInfo: {
      type: Boolean,
      default: true
    },
    showSendButton: {
      type: Boolean,
      default: true
    },

    // Options de comportement
    autoFocus: {
      type: Boolean,
      default: false
    },
    autoJoin: {
      type: Boolean,
      default: true
    },

    // Personnalisation
    placeholder: {
      type: String,
      default: 'Type a message...'
    },
    maxLength: {
      type: Number,
      default: 2000
    },

    // Configuration personnalisée
    config: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['close', 'conversation-changed', 'message-sent'],
  setup(props, { emit }) {
    // État du composant
    const messagesContainer = ref(null)
    const isSearchOpen = ref(false)
    const searchQuery = ref('')

    // Composable de chat
    const chat = useChat(props.config)

    // Computed
    const containerClasses = computed(() => ({
      'chat-container--connected': chat.isConnected.value,
      'chat-container--loading': chat.isLoading.value,
      'chat-container--error': !!chat.error.value,
      'chat-container--search-open': isSearchOpen.value
    }))

    const conversationName = computed(() => {
      if (!chat.currentConversation.value) return 'Chat'
      return chat.currentConversation.value.name || 'Unnamed Chat'
    })

    const conversationAvatar = computed(() => {
      if (!chat.currentConversation.value) return null

      // Pour les conversations directes, utiliser l'avatar de l'autre utilisateur
      if (chat.currentConversation.value.type === 'direct') {
        const otherUser = chat.currentConversation.value.participants.find(
          p => p.id !== chat.currentUser.value?.id
        )
        return otherUser?.avatar
      }

      return chat.currentConversation.value.metadata?.avatar
    })

    const conversationInitials = computed(() => {
      const name = conversationName.value
      return name
        .split(' ')
        .map(word => word[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    })

    const onlineStatus = computed(() => {
      if (!chat.currentConversation.value) return null

      // Pour les conversations directes, afficher le statut
      if (chat.currentConversation.value.type === 'direct') {
        const otherUser = chat.currentConversation.value.participants.find(
          p => p.id !== chat.currentUser.value?.id
        )
        return otherUser?.isOnline ? 'Online' : 'Offline'
      }

      return null
    })

    const participantCount = computed(() => {
      if (!chat.currentConversation.value) return ''
      const count = chat.currentConversation.value.participants.length
      return count > 1 ? `${count} participants` : '1 participant'
    })

    const inputPlaceholder = computed(() => {
      if (!chat.isConnected.value) return 'Connecting...'
      if (chat.isLoading.value) return 'Loading...'
      return props.placeholder
    })

    const filteredMessages = computed(() => {
      if (!searchQuery.value.trim()) {
        return chat.messages.value
      }

      const query = searchQuery.value.toLowerCase()
      return chat.messages.value.filter(message =>
        message.text.toLowerCase().includes(query)
      )
    })

    // Méthodes
    const initializeChat = async () => {
      try {
        if (props.conversationId) {
          // Rejoindre une conversation existante
          await chat.joinConversation(props.conversationId)
        } else if (props.participants.length > 0) {
          // Créer une nouvelle conversation
          const conversation = await chat.createConversation(props.participants)
          await chat.joinConversation(conversation.id)
          emit('conversation-changed', conversation)
        }
      } catch (error) {
        console.error('Failed to initialize chat:', error)
      }
    }

    let scrollTimeout = null
    let lastScrollHeight = 0

    const scrollToBottom = (force = false) => {
      if (!messagesContainer.value) return

      // Éviter les scrolls multiples et inutiles
      const currentScrollHeight = messagesContainer.value.scrollHeight
      if (!force && Math.abs(currentScrollHeight - lastScrollHeight) < 50) {
        return // Pas assez de changement pour justifier un scroll
      }

      // Annuler le scroll précédent
      if (scrollTimeout) {
        clearTimeout(scrollTimeout)
      }

      scrollTimeout = setTimeout(() => {
        if (messagesContainer.value) {
          const scrollHeight = messagesContainer.value.scrollHeight
          messagesContainer.value.scrollTop = scrollHeight
          lastScrollHeight = scrollHeight
        }
      }, 100) // Attendre que le contenu soit stabilisé
    }

    const handleSendMessage = async (text, attachments) => {
      try {
        const message = await chat.sendMessage(text, attachments)
        emit('message-sent', message)
        scrollToBottom()
      } catch (error) {
        console.error('Failed to send message:', error)
      }
    }

    const handleMessageAction = (action, message) => {
      console.log('Message action:', action, message)
      // Gérer les actions sur les messages (edit, delete, reply, etc.)
    }

    const loadMoreMessages = async () => {
      if (!chat.currentConversation.value || chat.isLoading.value) return

      try {
        const oldestMessage = chat.messages.value[0]
        await chat.loadMessages(
          chat.currentConversation.value.id,
          50,
          oldestMessage?.timestamp
        )
      } catch (error) {
        console.error('Failed to load more messages:', error)
      }
    }

    const toggleSearch = () => {
      isSearchOpen.value = !isSearchOpen.value
      if (!isSearchOpen.value) {
        searchQuery.value = ''
      }
    }

    const handleSearch = () => {
      // La recherche est gérée par le computed filteredMessages
    }

    const showInfoPanel = () => {
      // Implémenter un panneau d'informations
      console.log('Show info panel')
    }

    const handleAttachment = (files) => {
      console.log('Handle attachments:', files)
      // Implémenter la gestion des pièces jointes
    }

    // Watchers
    watch(() => chat.messages.value, (newMessages, oldMessages) => {
      // Scroller seulement si de nouveaux messages sont ajoutés (pas si on charge d'anciens messages)
      if (newMessages.length > (oldMessages?.length || 0)) {
        scrollToBottom(true)
      }
    }, { deep: true })

    watch(() => props.conversationId, async (newId) => {
      if (newId && newId !== chat.currentConversation.value?.id) {
        await initializeChat()
      }
    })

    // Cycle de vie
    onMounted(async () => {
      await initializeChat()

      if (props.autoFocus) {
        // Focus sur l'input après initialisation
        nextTick(() => {
          const input = document.querySelector('.message-input')
          if (input) input.focus()
        })
      }
    })

    return {
      // Références
      messagesContainer,

      // État du composable
      ...chat,

      // État local
      isSearchOpen,
      searchQuery,

      // Computed
      containerClasses,
      conversationName,
      conversationAvatar,
      conversationInitials,
      onlineStatus,
      participantCount,
      inputPlaceholder,
      filteredMessages,

      // Méthodes
      handleSendMessage,
      handleMessageAction,
      loadMoreMessages,
      toggleSearch,
      handleSearch,
      showInfoPanel,
      handleAttachment
    }
  }
}
</script>

<style scoped>
.chat-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  position: relative;
}

.chat-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  border-bottom: 1px solid #e5e7eb;
  background: white;
  flex-shrink: 0;
}

.chat-header-content {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.chat-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
}

.avatar-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-placeholder {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 0.875rem;
}

.chat-info {
  flex: 1;
  min-width: 0;
}

.chat-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.chat-subtitle {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #6b7280;
  margin-top: 0.125rem;
}

.online-indicator {
  width: 8px;
  height: 8px;
  background: #10b981;
  border-radius: 50%;
  flex-shrink: 0;
}

.chat-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.action-button {
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s;
  font-size: 1rem;
}

.action-button:hover {
  background: #f3f4f6;
}

.close-button:hover {
  background: #fee2e2;
  color: #dc2626;
}

.chat-search {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
}

.search-input {
  flex: 1;
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  outline: none;
}

.search-input:focus {
  border-color: hsl(var(--primary));
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.search-close {
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #6b7280;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.loading-container,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  text-align: center;
  color: #6b7280;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #e5e7eb;
  border-top: 3px solid hsl(var(--primary));
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.empty-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

.empty-state h3 {
  margin: 0 0 0.5rem 0;
  font-size: 1.125rem;
  color: #374151;
}

.chat-input-container {
  padding: 1rem;
  border-top: 1px solid #e5e7eb;
  background: white;
  flex-shrink: 0;
}

.chat-error {
  position: absolute;
  bottom: 1rem;
  left: 1rem;
  right: 1rem;
  background: #fee2e2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  padding: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #dc2626;
  font-size: 0.875rem;
}

.error-icon {
  flex-shrink: 0;
}

.error-text {
  flex: 1;
}

.error-close {
  background: transparent;
  border: none;
  color: #dc2626;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
}

.error-close:hover {
  background: #fecaca;
}

/* Responsive */
@media (max-width: 640px) {
  .chat-container {
    /* height: 100vh; */
    border-radius: 0;
  }

  .chat-header {
    padding: 0.75rem;
  }

  .chat-actions {
    gap: 0.25rem;
  }

  .action-button {
    width: 28px;
    height: 28px;
    font-size: 0.875rem;
  }
}
</style>